<script setup lang="ts">
/**
 * Tool detail + try-it: a form generated from the tool's JSON input schema.
 * Calls POST /api/v2/tools/:name/call as the signed-in user; changes never
 * run directly from here — they come back as "needs approval".
 * `?call=<id>` fills the form from one of your logged calls (My activity).
 */
import { AppWindow, Braces, FormInput, History, Loader, Play, RotateCcw, SearchX } from "@lucide/vue";
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { toolAppName } from "@/api/apps";
import { argsForForm, callNeedsReview, useMyCall } from "@/api/calls";
import { callTool, useMyTools } from "@/api/me";
import type { ToolCallResult } from "@/api/contracts";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import FormError from "@/components/common/FormError.vue";
import JsonView from "@/components/common/JsonView.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import SchemaForm from "@/components/forms/SchemaForm.vue";
import AccessBadge from "@/components/tools/AccessBadge.vue";
import ToolResult from "@/components/tools/ToolResult.vue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { t } from "@/copy/strings";
import { domainInfo, domainOfTool } from "@/lib/domains";
import { formatDateTime } from "@/lib/format";
import { cleanValues, defaultsFor, missingRequired } from "@/lib/schema";
import { toolAccess, toolTargets } from "@/lib/tools";

definePage({ meta: { title: "Tool", layout: "wide" } });

const route = useRoute("/tools/[name]");
const tools = useMyTools();
const tool = computed(() => tools.data.value?.find((x) => x.name === route.params.name));
const domain = computed(() => domainInfo(domainOfTool(route.params.name)));
const access = computed(() => (tool.value ? toolAccess(tool.value) : "read"));
const targets = computed(() => (tool.value ? toolTargets(tool.value) : []));

const mode = ref<"form" | "json">("form");
const values = ref<Record<string, unknown>>({});
const jsonText = ref("{}");
const jsonError = ref<string | null>(null);
const running = ref(false);
const error = ref<unknown>(null);
const result = ref<ToolCallResult | null>(null);
const resultArgs = ref<Record<string, unknown>>({});
const appName = computed(() => toolAppName(tool.value));
const missing = ref<string[]>([]);

/** Arguments from the URL (?upload=upload://…&engine=agent), e.g. from the Files page's "Use with". */
function fromQuery(): Record<string, unknown> {
  const props = tool.value?.inputSchema.properties ?? {};
  const out: Record<string, unknown> = {};
  for (const [key, raw] of Object.entries(route.query)) {
    const prop = props[key];
    const value = Array.isArray(raw) ? raw[0] : raw;
    if (!prop || typeof value !== "string") continue;
    const type = Array.isArray(prop.type) ? prop.type[0] : prop.type;
    if (type === "array") out[key] = [value];
    else if (type === "number" || type === "integer") out[key] = Number(value);
    else if (type === "boolean") out[key] = value === "true";
    else out[key] = value;
  }
  return out;
}

/** A logged call to start from (?call=<id> from My activity): its redacted arguments. */
const callId = computed(() => {
  const raw = Array.isArray(route.query.call) ? route.query.call[0] : route.query.call;
  const id = raw ? Number(raw) : Number.NaN;
  return Number.isInteger(id) && id > 0 ? id : null;
});
const prefill = useMyCall(callId);
const prefillCall = computed(() => (prefill.data.value && prefill.data.value.tool === tool.value?.name ? prefill.data.value : null));

function fromCall(): Record<string, unknown> {
  return prefillCall.value && tool.value ? argsForForm(prefillCall.value, tool.value.inputSchema) : {};
}

function reset() {
  values.value = tool.value ? { ...defaultsFor(tool.value.inputSchema), ...fromQuery(), ...fromCall() } : {};
  jsonText.value = JSON.stringify(values.value, null, 2);
  result.value = null;
  error.value = null;
  missing.value = [];
}
watch([tool, prefillCall], reset, { immediate: true });

watch(mode, (m) => {
  if (m === "json") jsonText.value = JSON.stringify(cleanValues(values.value, tool.value?.inputSchema ?? {}), null, 2);
  else {
    try {
      values.value = JSON.parse(jsonText.value || "{}");
      jsonError.value = null;
    } catch {
      /* keep form values */
    }
  }
});

async function run() {
  if (!tool.value) return;
  let args: Record<string, unknown>;
  if (mode.value === "json") {
    try {
      args = JSON.parse(jsonText.value || "{}");
      jsonError.value = null;
    } catch (err) {
      jsonError.value = (err as Error).message;
      return;
    }
  } else {
    args = cleanValues(values.value, tool.value.inputSchema);
    missing.value = missingRequired(args, tool.value.inputSchema);
    if (missing.value.length) return;
  }
  running.value = true;
  error.value = null;
  result.value = null;
  try {
    resultArgs.value = args;
    result.value = await callTool(tool.value.name, args);
  } catch (err) {
    error.value = err;
  } finally {
    running.value = false;
  }
}
</script>

<template>
  <div>
    <ListSkeleton v-if="tools.isPending.value" :rows="4" />
    <ErrorState v-else-if="tools.error.value" :error="tools.error.value" @retry="tools.refetch()" />
    <EmptyState
      v-else-if="!tool"
      :icon="SearchX"
      title="Tool not available"
      :description="`${route.params.name} doesn't exist or isn't granted to you. Ask an admin if you need it.`"
    >
      <Button as-child variant="outline"><RouterLink to="/tools">{{ t.tools.title }}</RouterLink></Button>
    </EmptyState>
    <template v-else>
      <PageHeader
        :title="tool.title ?? tool.name"
        :crumbs="[{ label: t.tools.title, to: '/tools' }, { label: domain.label, to: `/tools?domain=${encodeURIComponent(domain.label)}` }, { label: tool.name }]"
      >
        <template #badges>
          <AccessBadge :access="access" />
          <Badge v-if="tool.execution?.taskSupport && tool.execution.taskSupport !== 'forbidden'" variant="info">Runs in background</Badge>
          <RouterLink v-if="appName" :to="{ path: '/apps', query: { app: appName } }" class="rounded-md focus-visible:ring-3 focus-visible:ring-ring/40 focus-visible:outline-none">
            <Badge variant="outline" class="hover:bg-accent"><AppWindow aria-hidden="true" /> {{ t.apps.badge }}</Badge>
          </RouterLink>
        </template>
        <template #meta>
          <p class="mt-1 font-mono text-xs text-muted-foreground">{{ tool.name }}</p>
        </template>
      </PageHeader>

      <div class="grid gap-6 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        <section class="flex min-w-0 flex-col gap-5" aria-labelledby="try-heading">
          <p v-if="tool.description" class="text-sm leading-6 text-muted-foreground">{{ tool.description }}</p>
          <div v-if="targets.length" class="flex flex-col gap-2">
            <p class="eyebrow">{{ t.tools.targets }}</p>
            <div class="flex flex-wrap gap-1.5">
              <Badge v-for="target in targets" :key="target" variant="outline" class="font-mono">{{ target }}</Badge>
            </div>
          </div>

          <Card padding="none" class="p-4 sm:p-5">
            <div class="mb-4 flex items-center gap-2">
              <h2 id="try-heading" class="section-title">{{ t.tools.tryIt }}</h2>
              <ToggleGroup v-model="mode" type="single" size="sm" variant="outline" class="ml-auto" aria-label="Input mode">
                <ToggleGroupItem value="form" aria-label="Form"><FormInput class="size-3.5" /> {{ t.tools.form }}</ToggleGroupItem>
                <ToggleGroupItem value="json" aria-label="JSON"><Braces class="size-3.5" /> JSON</ToggleGroupItem>
              </ToggleGroup>
            </div>
            <p v-if="access === 'write'" class="mb-4 rounded-md bg-warning-soft/70 px-3 py-2 text-xs">{{ t.tools.writeNote }}</p>
            <div v-if="callId !== null && !prefill.isPending.value" class="mb-4 flex items-start gap-2 rounded-md border bg-muted/50 px-3 py-2 text-xs" role="status">
              <History class="mt-0.5 size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
              <p v-if="prefillCall">
                {{ t.tools.prefilled }} ({{ formatDateTime(prefillCall.createdAt) }}<template v-if="prefillCall.target">, {{ prefillCall.target }}</template>).
                <template v-if="callNeedsReview(prefillCall)">{{ t.tools.prefillReview }}</template>
              </p>
              <p v-else-if="prefill.data.value">{{ t.tools.prefillOtherTool }}</p>
              <p v-else>{{ t.tools.prefillMissing }}</p>
            </div>
            <form class="flex flex-col gap-4" @submit.prevent="run">
              <SchemaForm v-if="mode === 'form'" v-model="values" :schema="tool.inputSchema" id-prefix="tool" :disabled="running" />
              <template v-else>
                <Textarea v-model="jsonText" rows="10" class="font-mono text-[13px]" :aria-label="t.tools.arguments" :aria-invalid="Boolean(jsonError)" />
                <p v-if="jsonError" class="text-xs text-destructive">Invalid JSON: {{ jsonError }}</p>
              </template>
              <p v-if="missing.length" class="text-sm text-destructive" role="alert">Please fill in: {{ missing.join(", ") }}</p>
              <FormError :error="error" />
              <div class="flex gap-2">
                <Button type="submit" :disabled="running">
                  <Loader v-if="running" class="animate-spin" /><Play v-else />
                  {{ running ? t.tools.calling : t.tools.call }}
                </Button>
                <Button type="button" variant="ghost" :disabled="running" @click="reset"><RotateCcw /> Reset</Button>
              </div>
            </form>
          </Card>

          <details class="rounded-lg border bg-card p-3 text-sm">
            <summary class="cursor-pointer font-medium">Input schema</summary>
            <JsonView class="mt-3" :value="tool.inputSchema" label="input schema" />
          </details>
        </section>

        <section class="flex min-w-0 flex-col gap-3" aria-live="polite" aria-label="Result">
          <h2 class="section-title">{{ t.tools.result }}</h2>
          <div v-if="running" class="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
            <Loader class="mx-auto mb-2 size-5 animate-spin" aria-hidden="true" /> {{ t.tools.calling }}
          </div>
          <ToolResult v-else-if="result" :result="result" :app="appName" :tool-input="resultArgs" />
          <div v-else class="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">{{ t.tools.noResult }}</div>
        </section>
      </div>
    </template>
  </div>
</template>
